import { RefObject, useEffect } from 'react';

/**
 * Keeps the separately rendered sticky header table as wide as the body table.
 *
 * Column widths come from flex values, but a table grows past its container
 * to fit its content once the columns reach their min-width (e.g. an order
 * number that cannot wrap). The header and body tables hold different
 * content, so each would grow by a different amount and the columns would
 * drift apart. Both are given the wider of the two natural widths instead.
 *
 * Re-measured whenever the body's scroll container or table changes size
 * (viewport resize, rows loaded, columns toggled). The work is deferred to the
 * next frame so resizing the tables from inside the observer callback cannot
 * trigger a ResizeObserver loop error.
 */
export const useSyncStickyHeaderWidth = (
  isEnabled: boolean,
  headerScrollerRef: RefObject<HTMLDivElement>,
  headerTableRef: RefObject<HTMLTableElement>,
  bodyScrollerRef: RefObject<HTMLDivElement>,
  bodyTableRef: RefObject<HTMLTableElement>
) => {
  useEffect(() => {
    const headerScroller = headerScrollerRef.current;
    const headerTable = headerTableRef.current;
    const bodyScroller = bodyScrollerRef.current;
    const bodyTable = bodyTableRef.current;
    if (
      !isEnabled ||
      !headerScroller ||
      !headerTable ||
      !bodyScroller ||
      !bodyTable
    ) {
      return;
    }

    let frameId: number | null = null;

    const syncWidth = () => {
      frameId = null;
      // Clear the previous sync first, otherwise the tables could never
      // shrink back when the viewport widens or content gets narrower.
      headerTable.style.minWidth = '';
      bodyTable.style.minWidth = '';
      const width = Math.max(headerTable.offsetWidth, bodyTable.offsetWidth);
      headerTable.style.minWidth = `${width}px`;
      bodyTable.style.minWidth = `${width}px`;
      headerScroller.scrollLeft = bodyScroller.scrollLeft;
    };

    const scheduleSync = () => {
      if (frameId === null) {
        frameId = requestAnimationFrame(syncWidth);
      }
    };

    const resizeObserver = new ResizeObserver(scheduleSync);
    resizeObserver.observe(bodyScroller);
    resizeObserver.observe(bodyTable);

    return () => {
      resizeObserver.disconnect();
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [isEnabled]);
};
